import numeral from 'numeral';
import type { App } from 'vue';

import { type DateInputNullable, formatDate as formatDateUtil } from './date';

/**
 * Currency codes supported by the application
 */
type CurrencyCode = 'USD' | string;

/**
 * Money object structure
 */
type MoneyObject = {
  amount: number;
  currency: {
    code: CurrencyCode;
  };
};

/**
 * Get currency symbol from currency code
 */
const getCurrencySymbol = (_code: CurrencyCode): string => {
  return '$';
  /*
  switch (code) {
    case 'USD':
      return '$';
    default:
      return '$';
  }
   */
};

/**
 * Format number with wide decimal format
 * @param value - Numeric value to format
 * @param extraWide - If true, uses 4 decimal places instead of 2
 * @returns Formatted string with decimals or abbreviated notation (e.g., "1.5K")
 * @example
 * formatWideDecimal(1234.56) => "1234.56"
 * formatWideDecimal(123456) => "123.5K"
 * formatWideDecimal(0.1234, true) => "0.1234"
 */
// TODO: This function must return string values only!!!
export const formatWideDecimal = (value: number | string, extraWide?: boolean): string | number => {
  const number = Number(value);
  if (isNaN(number)) {
    return value;
  }
  const absValue = Math.abs(number);
  let format = absValue % 1 === 0 ? '0' : '0.00';
  if (extraWide && absValue % 1 !== 0) {
    format = '0.0000';
  }
  if (absValue < 1) {
    return numeral(number).format(format);
  }
  if (absValue >= 1 && absValue <= 99999) {
    return numeral(number).format(format);
  }
  if (absValue > 99999) {
    return numeral(number).format('0.0a').toUpperCase();
  }
  return value; // fallback
};

/**
 * Format integer with thousands separator
 * @param value - Integer value to format
 * @returns Formatted string with commas (e.g., "10,000")
 * @example
 * formatWideInt(10000) => "10,000"
 * formatWideInt(1234567) => "1,234,567"
 */
export const formatWideInt = (value: number | string): string | undefined => {
  const number = Number(value);
  if (isNaN(number)) {
    return;
  }
  return numeral(number).format('10,000').toUpperCase();
};

/**
 * Format integer to abbreviated symbols
 * @param value - Integer value to format
 * @returns Abbreviated string (e.g., "1.5K", "2.3M")
 * @example
 * formatIntToSymbols(1500) => "1.5K"
 * formatIntToSymbols(2300000) => "2.3M"
 */
export const formatIntToSymbols = (value: number | string): string | undefined => {
  const number = Number(value);
  if (isNaN(number)) {
    return;
  }
  return numeral(number).format('0[.]0a').toUpperCase();
};

/**
 * Format file size in bytes to human-readable format
 * @param size - File size in bytes
 * @returns Formatted string with appropriate unit (B, KB, MB, GB, TB)
 * @example
 * formatFileSize(1024) => "1.00 KB"
 * formatFileSize(1048576) => "1.00 MB"
 * formatFileSize(1073741824) => "1.00 GB"
 */
export const formatFileSize = (size: number): string => {
  if (size > 1024 * 1024 * 1024 * 1024) {
    return (size / 1024 / 1024 / 1024 / 1024).toFixed(2) + ' TB';
  } else if (size > 1024 * 1024 * 1024) {
    return (size / 1024 / 1024 / 1024).toFixed(2) + ' GB';
  } else if (size > 1024 * 1024) {
    return (size / 1024 / 1024).toFixed(2) + ' MB';
  } else if (size > 1024) {
    return (size / 1024).toFixed(2) + ' KB';
  }
  return size.toString() + ' B';
};

/**
 * Register all filters globally in Vue app
 */
export default function registerFilters(app: App): void {
  // Filters are converted to reusable functions
  const filters = {
    /**
     * Format money object to string
     * @param val - Money object with amount and currency
     * @returns Formatted money string or '-' if null
     * @example
     * formatMoneyObjToString({ amount: 100, currency: { code: 'USD' } }) => "$100"
     */
    formatMoneyObjToString(val: MoneyObject | null | undefined): string {
      return val ? `${getCurrencySymbol(val.currency.code)}${val.amount}` : '-';
    },
    /**
     * Capitalize first letter of string
     * @param value - String to capitalize
     * @returns Capitalized string
     * @example
     * capitalize('hello') => "Hello"
     */
    capitalize(value: string | number | null | undefined): string {
      if (!value) {
        return '';
      }
      return value.toString().charAt(0).toUpperCase() + value.toString().slice(1);
    },
    formatWideDecimal,
    formatIntToSymbols,
    formatFileSize,
    formatWideInt,
    /**
     * Convert string to uppercase
     * @param val - Value to convert
     * @returns Uppercased string or original value if not a string
     * @example
     * upperCase('hello') => "HELLO"
     */
    upperCase(val: string | number | null | undefined): string | number | null | undefined {
      if (typeof val === 'string') {
        return val.toUpperCase();
      }
      return val;
    },
    /**
     * Format date using formatDate utility
     * @param value - Date value to format
     * @returns Formatted date string
     */
    formatDate(value: DateInputNullable): string {
      return formatDateUtil(value);
    },
  };

  // Registering all filters globally using app.config.globalProperties
  app.config.globalProperties.$filters = filters;

  // Registering as reusable functions so that you can directly use them in your components
  Object.keys(filters).forEach((key) => {
    app.config.globalProperties[`$${key}`] = filters[key as keyof typeof filters];
  });
}
